import type { CitySuggestion } from '@cibli/shared';

import api from './api';

interface AutocompleteResponse {
  suggestions: CitySuggestion[];
}

interface ValidateResponse {
  status?: string;
  valid?: boolean;
}

/** Typed city/postal validation outcome (plan 2026-10-21). */
export type CityValidationStatus = 'verified' | 'invalid' | 'unavailable';

/**
 * Country of the device's BASE LOCALE (GH-599 / plan 2026-10-21): the city
 * search follows it — the DISPLAY LANGUAGE never changes the search country,
 * and the PHYSICAL IP country never overrides a configured base locale.
 * Precedence mirrors the boot-locale resolution (language.context):
 * - booth default locale (`es-MX` → `mx`) — the kiosk's configured base;
 * - web region default locale (`es-MX` → `mx`) — the region's configured base;
 * - web region country code (Cloudflare) — physical fallback when no locale
 *   is configured;
 * - otherwise undefined → the server falls back to CF-IPCountry, then France.
 */
export function resolveBaseCountry(params: {
  boothDefaultLocaleId?: string | null;
  webRegionDefaultLocale?: string | null;
  webRegionCountry?: string | null;
}): string | undefined {
  const extract = (value?: string | null): string | undefined => {
    const v = value?.trim().toLowerCase();
    if (!v) return undefined;
    if (/^[a-z]{2}$/.test(v)) return v;
    const region = v.includes('-') ? v.split('-')[1] : undefined;
    return region && /^[a-z]{2}$/.test(region) ? region : undefined;
  };

  return (
    extract(params.boothDefaultLocaleId) ??
    extract(params.webRegionDefaultLocale) ??
    extract(params.webRegionCountry)
  );
}

export const fetchCitySuggestions = async (
  text: string,
  limit = 15,
  country?: string,
  lang?: string,
): Promise<CitySuggestion[]> => {
  if (!text || text.trim().length === 0) {
    return [];
  }

  const normalized = text.trim().toLowerCase().replace(/-/g, ' ').replace(/\s+/g, ' ');

  if (normalized.length < 1) {
    return [];
  }

  try {
    const response = await api.get<AutocompleteResponse>(
      '/geocoding/autocomplete',
      {
        params: {
          q: normalized,
          maximumResponses: limit,
          ...(country ? { country } : {}),
          // UI language: localized place names (es-MX → Spanish, fr → French…)
          ...(lang ? { lang } : {}),
        },
      },
      { showErrorToast: false },
    );

    return response.suggestions || [];
  } catch (error) {
    console.error('Failed to fetch city suggestions:', error);
    return [];
  }
};

/**
 * Validates a city + postal-code couple (plan 2026-10-21) — TYPED status:
 * - `verified`   : a provider or the local reference confirmed the couple;
 * - `invalid`    : sources contradict it (city ≠ postal code);
 * - `unavailable`: no source could answer (network failure included) — the
 *   form shows a retry, then a manual continuation. An unavailable status is
 *   never silently accepted as verified (the old client-side lenient
 *   fallbacks — any-place Zippopotam, length checks — are gone).
 */
export const validateCityZipcode = async (
  city: string,
  zipcode: string,
  countryHint?: string,
): Promise<CityValidationStatus> => {
  const cleanCity = city?.trim();
  const cleanZipcode = zipcode?.trim();

  if (!cleanCity || cleanCity.length < 2 || !cleanZipcode || cleanZipcode.length < 3) {
    return 'invalid';
  }

  try {
    const response = await api.get<ValidateResponse>(
      '/geocoding/validate',
      {
        params: {
          city: cleanCity,
          postalCode: cleanZipcode,
          ...(countryHint ? { country: countryHint } : {}),
        },
      },
      { showErrorToast: false },
    );

    if (
      response.status === 'verified' ||
      response.status === 'invalid' ||
      response.status === 'unavailable'
    ) {
      return response.status;
    }
    // Compatibility with an older backend shape ({ valid } only).
    return response.valid ? 'verified' : 'invalid';
  } catch (error) {
    console.error('City validation request failed:', error);
    return 'unavailable';
  }
};
